"use client";

import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons, Images } from "@/app/assets/shared";
import Image, { type StaticImageData } from "next/image";
import SectionHeader from "@/components/SectionHeader";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
// PERF: GSAP + ScrollTrigger loaded lazily via ensureGsap() (below-the-fold
// pinned scroll animation; registerPlugin after mount). Behavior unchanged.
import { ensureGsap } from "@/lib/EnsureGsap";
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";
import EmergingTechnologySSR from "./EmergingTechnologySSR";

type TechItem = {
  title: string;
  description: string;
  image: string | StaticImageData;
};

interface emergingTechProps {
  TechArray: TechItem[];
  ctaButton?: React.ReactNode;
  description: string;
  title: React.ReactNode;
  showSubtitle?: boolean;
}
const defaultCta = (
  <PrimaryBtn
    text="Let's Talk with us"
    dark={true}
    iconSrc={Icons?.chevronBlue}
  />
);

// The pinned desktop layout lives in its own component ON PURPOSE.
//
// ScrollTrigger `pin: true` re-parents the pinned element into a `pin-spacer`
// div it injects itself. If that happens inside a subtree React later swaps out
// (crossing the 1365px breakpoint, e.g. opening devtools), React removes/inserts
// nodes against parents GSAP has since changed and throws
// `NotFoundError: Failed to execute 'insertBefore' ...`.
//
// Two things keep that from happening:
//  1. The GSAP lifecycle is a `useLayoutEffect` in the component that unmounts.
//     React runs a deleted function component's layout cleanup BEFORE it detaches
//     that component's host nodes, so `ctx.revert()` puts the DOM back first.
//     A passive `useEffect` (or a layout effect in the PARENT) runs too late —
//     child DOM is already gone by then. That was the bug.
//  2. The pin target is wrapped in a plain div owned by this component, so even
//     if a revert were ever missed, the injected pin-spacer is removed along
//     with the wrapper instead of orphaning a node React still tracks.
function PinnedTechList({ TechArray }: { TechArray: TechItem[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (TechArray.length === 0) return;

    const listContainer = listContainerRef.current;
    const list = listRef.current;

    if (!listContainer || !list) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    ensureGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;

      let ctx = gsap.context(() => {
        const items = Array.from(
          list.querySelectorAll(".tech-item"),
        ) as HTMLElement[];
        const containerHeight = listContainer.offsetHeight;
        const containerCenterY = containerHeight / 2;

        const offsets = items?.map((el) => {
          const itemTopRelativeToList = el.offsetTop;
          const itemHeight = el.offsetHeight;
          return -(itemTopRelativeToList + itemHeight / 2 - containerCenterY);
        });

        const startY = offsets[0];
        const endY = offsets[offsets.length - 1];

        gsap
          .timeline({
            scrollTrigger: {
              trigger: listContainer,
              start: "top top",
              end: "+=3000",
              pin: true,
              scrub: 1.5,
              pinSpacing: true,
              invalidateOnRefresh: true,
              refreshPriority: 1, // Ensures this section calculates its position correctly relative to others
              onUpdate: (self) => {
                const progress = self.progress;
                const closestIdx = Math.round(
                  progress * (TechArray.length - 1),
                );
                setActiveIndex(closestIdx);
              },
            },
          })
          .fromTo(list, { y: startY }, { y: endY, ease: "none" });
      });

      // --- DYNAMIC HEIGHT STABILIZER ---
      let refreshTimeout: NodeJS.Timeout;
      const debouncedRefresh = () => {
        clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(() => {
          // Only refresh if the user isn't actively scrolling to prevent jitter
          if (!ScrollTrigger.isScrolling()) {
            ScrollTrigger.refresh();
          }
        }, 200);
      };

      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.height > 0) {
            debouncedRefresh();
          }
        }
      });

      if (document.body) {
        resizeObserver.observe(document.body);
      }

      cleanup = () => {
        ctx.revert();
        resizeObserver.disconnect();
        clearTimeout(refreshTimeout);
      };
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [TechArray.length]);

  return (
    // Wrapper owned by React; GSAP's pin-spacer is injected INSIDE it, never as
    // a sibling of nodes the parent tracks.
    <div className="w-full">
      <div
        ref={listContainerRef}
        className="flex flex-row gap-12 h-screen items-center"
      >
        {/* TEXT COLUMN */}
        <div className="h-full flex-[1.5] relative overflow-hidden">
          <div
            ref={listRef}
            className="w-full flex flex-col gap-[6vh] will-change-transform py-[25vh]"
          >
            {TechArray?.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <div
                  key={item?.title}
                  className="tech-item transition-all duration-700 ease-in-out"
                >
                  <h3
                    className={`clash-font leading-[1.1] font-[500] transition-all duration-700 ${isActive ? "text-[45px] md:text-[55px] text-white opacity-100 mb-6" : "text-[30px] md:text-[35px] text-white/30 opacity-50 mb-2"}`}
                  >
                    {item?.title}
                  </h3>

                  <div
                    className={`transition-all duration-700 ease-in-out overflow-hidden ${isActive ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}
                  >
                    <p
                      className="text-white/70 max-w-[650px] text-lg md:text-[22px] leading-[1.6]"
                      dangerouslySetInnerHTML={{
                        __html: item.description ?? "",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* STATIC IMAGE COLUMN */}
        <div className="flex-1 h-full flex items-center justify-end">
          <div className="relative w-full aspect-[4/3] min-h-[500px] 2xl:min-h-[650px] overflow-hidden rounded-2xl ring-1 ring-white/10 bg-[#111] shadow-2xl">
            {TechArray?.map((item, index) => {
              return (
                <Image
                  key={item.title}
                  src={item.image}
                  alt={item.title}
                  fill
                  className={`object-cover transition-all duration-1000 absolute inset-0
                      ${index === activeIndex ? "opacity-100 scale-100 blur-0" : "opacity-0 scale-110 blur-sm"}`}
                  sizes="50vw"
                  loading={index === activeIndex ? "eager" : "lazy"}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmergingTechnology({
  TechArray = [],
  title,
  description,
  ctaButton = defaultCta,
  showSubtitle = true,
}: emergingTechProps) {
  const [isSimpleLayout, setIsSimpleLayout] = useState(false);
  const [mounted, setMounted] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);

  useTheme();

  // Gate client-only markup behind a post-mount flag so the FIRST client render
  // matches the server HTML (avoids hydration mismatch). Reading
  // `typeof window` directly in render diverges server vs. client and throws.
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 1365px)");
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsSimpleLayout(e.matches);
    handleChange(mql);
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // The breakpoint swap happens INSIDE a root element that is always present, so
  // React only ever mutates this component's own children — it never has to
  // insert/remove a node in the page container, where a neighbouring pinned
  // section's `pin-spacer` could have invalidated its sibling references.
  if (isSimpleLayout) {
    return (
      <div className="w-full bg-transparent">
        <EmergingTechnologySSR
          TechArray={TechArray}
          ctaButton={ctaButton}
          title={title}
          description={description}
          isServer={!mounted}
        />
      </div>
    );
  }

  return (
    <div className="w-full bg-transparent">
      <section
        ref={sectionRef}
        id="emerging-tech"
        className="bg-transparent w-full relative"
      >
        <Image
          src={Images?.serviceBg}
          alt="bg"
          fill
          className="object-cover -z-10"
          loading="lazy"
          sizes="(max-width: 1024px) 100vw, 50vw"
        />

        <div className="max-w-[5000px] mx-auto w-[92%] md:w-[calc(100%_-_10%)] flex flex-col pt-12 relative z-1">
          {mounted && (
            <div className="mb-10">
              <SectionHeader
                title={title}
                subtitle="Blending Innovation"
                paragraph={description}
                button={ctaButton}
                dark={true}
                showSubtitle={showSubtitle}
              />
              <div className="w-full h-[1px] bg-[#b5b5b5] opacity-30 mt-10" />
            </div>
          )}

          {!mounted ? (
            <EmergingTechnologySSR
              TechArray={TechArray}
              ctaButton={ctaButton}
              title={title}
              description={description}
              isServer={!mounted}
            />
          ) : (
            <PinnedTechList TechArray={TechArray} />
          )}
          <div className="h-[5vh]" />
        </div>
      </section>
    </div>
  );
}
